import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { login } from "../utils/auth";
import { Loader2 } from "lucide-react"; // スピナー表示用
import { clearEnglishOperatorSurface, isEnglishOperatorMode, isMainSalesBreakerHost, rememberOperatorSurfaceFromLocation, withOperatorSurface } from "../utils/operatorMode";
import { BOOKING_MANAGER_ROLE, getDefaultAuthenticatedPath } from "../constants/menuDefs";

const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

/**
 * Meta広告タグ（fbp/fbc）を取得するためのクッキー取得関数
 */
const getCookie = (name) => {
  try {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
  } catch (e) { return null; }
  return null;
};

const parseJwtPayload = (token) => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(window.atob(normalized));
  } catch (e) {
    console.error("JWT payload parse error:", e);
    return null;
  }
};

const getSafeOAuthReturn = (search) => {
  const raw = new URLSearchParams(search).get("oauth_return");
  if (!raw) return null;

  try {
    const parsed = new URL(raw, window.location.origin);
    if (parsed.origin !== window.location.origin) return null;
    if (parsed.pathname !== "/oauth/authorize") return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch (e) {
    return null;
  }
};

const createOAuthBridgeError = () => {
  const error = new Error("oauth_bridge_failed");
  error.isOAuthBridgeError = true;
  return error;
};

function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // 🚀 OEMドメインかどうかの判定（本家 salesbreaker.jp 以外なら true）
  const isOemDomain = !isMainSalesBreakerHost(window.location.hostname);
  const fallbackBrandName = isOemDomain ? "Partner Portal" : "SalesBreaker";
  const [oemBranding, setOemBranding] = useState(null);
  const [error, setError] = useState(null);
  const [isUnverifiedEmail, setIsUnverifiedEmail] = useState(false);
  const [resendStatus, setResendStatus] = useState(null);
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true); // 初期描画を隠すためのフラグ
  const location = useLocation();
  const isEnglishBeta = isEnglishOperatorMode() && !isOemDomain;

  // URLにトークンが含まれているか（自動ログイン中か）を判定
  const params = new URLSearchParams(location.search);
  const autoLoginToken = params.get("token");
  const isAutoLoginProcessing = !!autoLoginToken;

  // --- 初期表示の制御とトークン処理 ---
  useEffect(() => {
    rememberOperatorSurfaceFromLocation(location.search);

    const fetchBranding = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/oem/branding-by-domain?hostname=${window.location.hostname}`);
        const data = await res.json();
        
        if (data.success && data.is_oem) {
          setOemBranding(data.branding);
          // ブラウザのタブ名（タイトル）を書き換える
          document.title = `${data.branding.display_name} | ログイン`;

          // --- 🚀 【新規】ファビコン（アイコン）の動的書き換え ---
          if (data.branding.logo_path) {
            const iconUrl = `${window.location.origin}/api/oem${data.branding.logo_path}`;
            
            // 既存の <link rel="icon"> をすべて探して削除
            const existingIcons = document.querySelectorAll("link[rel*='icon']");
            existingIcons.forEach(el => el.parentNode.removeChild(el));

            // 新しいファビコン用タグを作成して <head> に追加
            const link = document.createElement('link');
            link.type = 'image/png';
            link.rel = 'shortcut icon';
            link.href = iconUrl;
            document.getElementsByTagName('head')[0].appendChild(link);
          }
        }
      } catch (e) {
        console.error("Branding fetch error:", e);
      }
    };

    fetchBranding();
    if (isEnglishBeta) {
      document.title = "SalesBreaker | English Operator Login";
    }

    if (autoLoginToken) {
      // 自動ログイン処理（トークンがある場合）
      const processTokenLogin = async () => {
        try {
          localStorage.setItem("authToken", autoLoginToken);
          const tokenPayload = parseJwtPayload(autoLoginToken);
          
          let retryCount = 0;
          while (retryCount < 20) {
            if (localStorage.getItem("authToken") === autoLoginToken) {
              break;
            }
            await new Promise(resolve => setTimeout(resolve, 50));
            retryCount++;
          }

          if (tokenPayload?.role === BOOKING_MANAGER_ROLE && tokenPayload?.id && tokenPayload?.client_id) {
            localStorage.setItem("userInfo", JSON.stringify(tokenPayload));
            setTimeout(() => {
              window.location.replace(`/app${getDefaultAuthenticatedPath(tokenPayload)}`);
            }, 300);
            return;
          }

          const res = await fetch(`${API_URL}/api/users`, {
            headers: { 'Authorization': `Bearer ${autoLoginToken}` } 
          });
          const data = await res.json();
          
          if (data.success && data.users.length > 0) {
            const matchedUser = tokenPayload?.id
              ? data.users.find((candidate) => Number(candidate.id) === Number(tokenPayload.id))
              : null;
            const currentUser = matchedUser || data.users[0];

            localStorage.setItem("userInfo", JSON.stringify(currentUser));
            
            setTimeout(() => {
              window.location.replace(`/app${getDefaultAuthenticatedPath(currentUser)}`);
            }, 300);
          } else {
            setError(isEnglishBeta ? "Could not load your user profile. Please try signing in again." : "ユーザー情報の取得に失敗しました。");
            setIsInitializing(false);
          }
        } catch (e) {
          console.error("Token login effect error:", e);
          setError(isEnglishBeta ? "Could not sync your authentication session. Please try again." : "認証情報の同期中にエラーが発生しました。");
          setIsInitializing(false);
        }
      };

      processTokenLogin();
    } else {
      const timer = setTimeout(() => {
        setIsInitializing(false);
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [autoLoginToken, isEnglishBeta, location.search]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setIsUnverifiedEmail(false);
    setResendStatus(null);
    try {
      const loggedInUser = await login(email, password);
      const oauthReturn = getSafeOAuthReturn(location.search);
      if (oauthReturn) {
        const token = localStorage.getItem("authToken");
        if (!token) throw createOAuthBridgeError();

        const bridgeResponse = await fetch(`${API_URL}/oauth/session`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          credentials: "same-origin",
        });

        if (!bridgeResponse.ok) throw createOAuthBridgeError();

        window.location.replace(oauthReturn);
        return;
      }

      const from = loggedInUser?.role === BOOKING_MANAGER_ROLE
        ? getDefaultAuthenticatedPath(loggedInUser)
        : location.state?.from?.pathname || getDefaultAuthenticatedPath(loggedInUser);
      window.location.replace('/app' + from);
    } catch (err) {
      if (err?.isOAuthBridgeError) {
        setError(isEnglishBeta
          ? "We could not complete the OpenAI authorization session. Please try signing in again."
          : "OpenAI連携用の認証セッションを作成できませんでした。もう一度ログインしてください。");
        setIsLoading(false);
        return;
      }

      const message = err.message || "";
      const unverified = /認証されていません|verify|verified/i.test(message);
      if (isEnglishBeta && unverified) {
        setIsUnverifiedEmail(true);
        setError("Your email address is not verified yet. Open the verification link we sent, or resend it below.");
      } else {
        setError(isEnglishBeta ? "Login failed. Please check your email and password." : message);
      }
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setResendStatus({ type: "error", message: "Enter your email address first." });
      return;
    }

    setIsResendingVerification(true);
    setResendStatus(null);
    try {
      const response = await fetch(`${API_URL}/api/resend-verification-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          surface: "english_beta",
          operator_surface: "english_beta",
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Could not resend the verification email.");
      setResendStatus({
        type: "success",
        message: "If this account is waiting for verification, a new email has been sent. Check your inbox and spam folder.",
      });
    } catch (resendError) {
      setResendStatus({ type: "error", message: resendError.message || "Could not resend the verification email." });
    } finally {
      setIsResendingVerification(false);
    }
  };

  const handleGoogleLogin = () => {
    const fbp = getCookie('_fbp');
    const fbc = getCookie('_fbc');
    const params = new URLSearchParams({
      signup_source: isEnglishBeta ? "english_beta_google" : "organic_web",
      fbp: fbp || "",
      fbc: fbc || "",
    });
    if (isEnglishBeta) {
      params.set("surface", "english_beta");
      params.set("operator_surface", "english_beta");
    }
    window.location.href = `${API_URL}/api/auth/google?${params.toString()}`;
  };

  const correctionSignupPath = withOperatorSurface(`/signup${email ? `?email=${encodeURIComponent(email)}` : ""}`);

  const handleJapaneseReset = () => {
    clearEnglishOperatorSurface();
    const nextUrl = new URL(window.location.href);
    nextUrl.pathname = "/app/login";
    nextUrl.searchParams.delete("surface");
    nextUrl.searchParams.delete("operator_surface");
    window.location.replace(nextUrl.pathname + nextUrl.search + nextUrl.hash);
  };

  // 自動ログイン中の表示
  if (isAutoLoginProcessing && !error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white font-sans text-slate-900">
        <Loader2 className="animate-spin text-indigo-600 mb-4" size={48} />
        <p className="text-slate-500 font-bold animate-pulse">{isEnglishBeta ? "Checking your operator session..." : "認証情報を確認しています..."}</p>
      </div>
    );
  }

  // 初期化中の真っ白な画面
  if (isInitializing && !error) {
    return <div className="min-h-screen bg-white"></div>;
  }

  return (
    <div className="relative flex items-center justify-center min-h-screen bg-gray-100 p-3 sm:p-4 font-sans text-slate-900">
      <div className="w-full max-w-md p-5 sm:p-8 bg-white shadow-xl rounded-2xl sm:rounded-3xl border border-slate-100">
        <div className="text-center mb-6 sm:mb-8">
            <div className="inline-block mb-4 h-16">
              <img 
                src={oemBranding?.logo_path ? `${window.location.origin}/api/oem${oemBranding.logo_path}` : "/images/logo.png"} 
                alt="Logo" 
                className="max-h-full w-auto mx-auto" 
                onError={(e) => { 
                  e.target.onerror = null; 
                  e.target.src=isOemDomain ? 'https://placehold.co/128x128/6366f1/ffffff?text=Logo' : 'https://placehold.co/128x128/6366f1/ffffff?text=SB'; 
                }}
              />
            </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight break-words" style={{ color: oemBranding?.brand_color || '#0f172a' }}>
            {oemBranding?.display_name || fallbackBrandName}
          </h1>
          <p className="text-slate-500 mt-2 font-medium">
            {isEnglishBeta ? "English Operator Login" : "🔐 アカウントにログイン"}
          </p>
          {isEnglishBeta && (
            <p className="mt-3 text-xs leading-5 text-slate-500">
              Operate governed outreach into Japanese companies. Japanese company data, Japanese outreach copy, and JST remain Japan-specific.
            </p>
          )}
        </div>

        <div className="space-y-6">
          <form className="space-y-4" onSubmit={handleLogin}>
            <div className="space-y-px rounded-xl overflow-hidden border border-slate-300 shadow-sm">
              <input 
                type="email" 
                required 
                className="w-full px-4 py-3 bg-white border-b border-slate-300 focus:ring-2 focus:ring-indigo-500 outline-none transition-all" 
                placeholder={isEnglishBeta ? "Email address" : "メールアドレス"} 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
              />
              <input 
                type="password" 
                required 
                className="w-full px-4 py-3 bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all" 
                placeholder={isEnglishBeta ? "Password" : "パスワード"} 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 font-medium">
                <p>{error}</p>
	                {isEnglishBeta && isUnverifiedEmail && (
	                  <div className="mt-3 rounded-lg bg-white/70 p-3 text-slate-700">
	                    <p className="text-xs leading-5 break-words">
	                      Is this email address correct?
	                      {email && <span className="mt-1 block font-bold text-slate-900">{email}</span>}
	                      Check your spam folder and promotions tab. If the address is wrong, create a new account with the correct email.
	                    </p>
	                    <div className="mt-2 flex flex-col gap-2 sm:flex-row">
	                      <button
	                        type="button"
	                        onClick={handleResendVerification}
	                        disabled={isResendingVerification}
	                        className="w-full sm:w-auto rounded-lg bg-slate-900 px-3 py-2 text-xs font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
	                      >
	                        {isResendingVerification ? "Sending..." : "Resend verification email"}
	                      </button>
	                      <Link
	                        to={correctionSignupPath}
	                        className="w-full sm:w-auto rounded-lg border border-slate-300 bg-white px-3 py-2 text-center text-xs font-bold text-slate-700 transition hover:bg-slate-50"
	                      >
	                        Wrong email? Create again
	                      </Link>
	                    </div>
	                    {resendStatus && (
	                      <p className={`mt-2 text-xs font-semibold ${resendStatus.type === "success" ? "text-emerald-700" : "text-red-600"}`}>
                        {resendStatus.message}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <button 
              type="submit" 
              disabled={isLoading} 
              className="w-full py-4 px-4 text-white rounded-xl font-bold transition-all shadow-lg active:scale-[0.98] disabled:bg-indigo-400"
              style={{ backgroundColor: oemBranding?.brand_color || '#4f46e5' }}
            >
              {isLoading ? (isEnglishBeta ? 'Signing in...' : '処理中...') : (isEnglishBeta ? 'Log in' : 'ログイン')}
            </button>
          </form>

          {!isOemDomain && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
                <div className="relative flex justify-center text-xs uppercase tracking-widest font-bold">
                  <span className="px-4 bg-white text-slate-400">{isEnglishBeta ? "or" : "または"}</span>
                </div>
              </div>

              <button 
                onClick={handleGoogleLogin} 
                type="button" 
                className="w-full flex items-center justify-center gap-3 py-4 px-4 border-2 border-slate-200 rounded-xl bg-white font-bold text-slate-700 hover:bg-slate-50 transition-all shadow-sm active:scale-[0.98]"
              >
                <img src="https://www.gstatic.com/images/branding/product/1x/gsa_512dp.png" className="w-5 h-5" alt="Google" />
                {isEnglishBeta ? "Continue with Google" : "Googleでログイン"}
              </button>
            </>
          )}
        </div>

        <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm font-bold">
          <Link to={withOperatorSurface("/forgot-password")} title={isEnglishBeta ? "Reset password" : "パスワードをリセット"} className="hover:underline" style={{ color: oemBranding?.brand_color || '#4f46e5' }}>
            {isEnglishBeta ? "Forgot password?" : "パスワードを忘れた"}
          </Link>
          
          {/* 🚀 修正：OEMドメインの場合は新規アカウント作成リンクを非表示にする */}
          {!isOemDomain && (
            <Link to={withOperatorSurface("/signup")} className="text-indigo-600 hover:underline">{isEnglishBeta ? "Create account" : "新規アカウント作成"}</Link>
          )}
        </div>

      </div>
      {isEnglishBeta && (
        <button
          type="button"
          onClick={handleJapaneseReset}
          className="fixed bottom-3 right-4 text-[11px] font-bold text-slate-300 hover:text-slate-600 hover:underline"
        >
          日本語
        </button>
      )}
    </div>
  );
}

export default LoginPage;
